import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface BeginUploadResponse {
  fileHandle: string;
}

export interface EndUploadResponse {
  blobUrl: string;
}

@Injectable({ providedIn: 'root' })
export class PlantsImageUploadService {
  private readonly CHUNK_SIZE = 1024 * 100; // 100KB
  private readonly baseUrl = environment.grpcHost;
  private readonly apiUrl = `${this.baseUrl}/api/PlantImage`;

  constructor(private http: HttpClient) { }

  private getHeaders(): HttpHeaders {
    const token = localStorage.getItem('token');
    return new HttpHeaders({
      'Authorization': token ? `Bearer ${token}` : '',
      'Content-Type': 'application/json'
    });
  }

  // Begin Upload → returns clean string
  beginUpload(): Observable<string> {
    return this.http.get(`${this.apiUrl}/BeginFileUpload`, {
      headers: this.getHeaders(),
      responseType: 'text'
    }).pipe(
      map(response => {
        try {
          return JSON.parse(response) as string;
        } catch {
          return response.trim().replace(/^"|"$/g, '');
        }
      })
    );
  }

  // Upload Chunk
  uploadChunk(fileHandle: string, data: string, startAt: number): Observable<any> {
    const body = { fileHandle, data, startAt };
    return this.http.put(`${this.apiUrl}/UploadChunk`, body, {
      headers: this.getHeaders()
    });
  }

  // End Upload
  endUpload(fileHandle: string, file: File): Observable<{ blobUrl: string }> {
    const extension = '.' + file.name.split('.').pop();
    const url = `${this.apiUrl}/EndFileUpload?fileHandle=${fileHandle}&quitUpload=false&fileSize=${file.size}&extension=${extension}`;
    return this.http.delete<{ blobUrl: string }>(url, {
      headers: this.getHeaders(),
      params: { fileName: file.name }
    });
  }

  // Helper Methods
  getChunkSize(): number {
    return this.CHUNK_SIZE;
  }

  getTotalChunks(file: File): number {
    return Math.ceil(file.size / this.CHUNK_SIZE);
  }

  getChunk(file: File, index: number): Blob {
    const start = index * this.CHUNK_SIZE;
    const end = Math.min(start + this.CHUNK_SIZE, file.size);
    return file.slice(start, end);
  }

  readChunkAsBase64(chunk: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve((reader.result as string).split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(chunk);
    });
  }
}
